import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '../../components/ui/Modal.jsx'
import Button from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import { Field, Input, Select, Textarea } from '../../components/ui/Field.jsx'
import api from '../../lib/api.js'

/*
 * Article create/edit modal — inventory §7. Required: code, description,
 * unit_price, vat_rate_id. Advanced (collapsed) section: note + one optional
 * allowance/charge. Internal modal (no browser dialog).
 */
const empty = {
  code: '', description: '', default_quantity: 1, unit_price: '', vat_rate_id: '',
  note: '', allowance_charge_amount: '', allowance_charge_reason: '', allowance_charge_is_percentage: false, is_charge: false,
}

export default function ArticleFormModal({ open, vatRates, entityId, initial, onClose, onSaved }) {
  const { t } = useTranslation()
  const [form, setForm] = useState(empty)
  const [advanced, setAdvanced] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const isEdit = !!entityId

  useEffect(() => {
    if (!open) return
    const seed = initial ? { ...empty, ...initial, vat_rate_id: initial.vat_rate_id ?? initial.vat_rate?.id ?? '' } : { ...empty, vat_rate_id: vatRates[0]?.id ?? '' }
    setForm(seed)
    setAdvanced(!!(initial && (initial.note || Number(initial.allowance_charge_amount) > 0)))
    setError('')
  }, [open, initial, vatRates])

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    const payload = {
      code: form.code.trim(), description: form.description.trim(),
      default_quantity: Number(form.default_quantity) || 1,
      unit_price: form.unit_price === '' ? null : Number(form.unit_price),
      vat_rate_id: Number(form.vat_rate_id),
      note: form.note || null,
      allowance_charge_amount: form.allowance_charge_amount ? Number(form.allowance_charge_amount) : null,
      allowance_charge_reason: form.allowance_charge_reason || null,
      allowance_charge_is_percentage: !!form.allowance_charge_is_percentage,
      is_charge: !!form.is_charge,
    }
    try {
      const { data } = isEdit ? await api.put(`/articles/${entityId}`, payload) : await api.post('/articles/', payload)
      onSaved?.(data?.article || data)
      onClose()
    } catch (err) {
      setError(err?.response?.data?.error || t('articles.toast.error'))
    } finally { setSubmitting(false) }
  }

  return (
    <Modal open={open} onClose={onClose} maxWidth={600} closeOnBackdrop={false}>
      <form onSubmit={submit}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '22px 24px 4px' }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 18, color: 'var(--color-ink)' }}>
            {isEdit ? t('articles.editTitle') : t('articles.newTitle')}
          </h3>
          <button type="button" onClick={onClose} aria-label={t('common.cancel')} style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)' }}><Icon name="x" size={20} /></button>
        </div>
        <div style={{ padding: '8px 24px 4px', maxHeight: '70vh', overflowY: 'auto' }}>
          {error && <div role="alert" style={{ background: 'var(--color-danger-bg)', color: 'var(--color-danger)', border: '1px solid #f3c9c9', borderRadius: 10, padding: '9px 12px', fontSize: 13, marginBottom: 14 }}>{error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label={t('articles.fields.code')} required><Input value={form.code} onChange={(e) => set('code', e.target.value)} required /></Field>
            <Field label={t('articles.fields.vatRate')} required>
              <Select value={form.vat_rate_id} onChange={(e) => set('vat_rate_id', e.target.value)} required>
                <option value="" disabled>—</option>
                {vatRates.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </Select>
            </Field>
          </div>
          <Field label={t('articles.fields.description')} required>
            <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={2} required />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label={t('articles.fields.defaultQuantity')}><Input type="number" step="0.01" min="0" value={form.default_quantity} onChange={(e) => set('default_quantity', e.target.value)} /></Field>
            <Field label={t('articles.fields.unitPrice')} required><Input type="number" step="0.01" min="0" value={form.unit_price} onChange={(e) => set('unit_price', e.target.value)} required /></Field>
          </div>

          <button type="button" onClick={() => setAdvanced((a) => !a)} style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'none', border: 'none', color: 'var(--color-primary)', fontWeight: 600, fontSize: 13.5, padding: '4px 0', marginBottom: 8 }}>
            <Icon name="chevron-down" size={16} style={{ transform: advanced ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} /> {t('articles.advanced')}
          </button>
          {advanced && (
            <div style={{ borderTop: '1px solid var(--color-border-row)', paddingTop: 12 }}>
              <Field label={t('articles.fields.note')}><Textarea value={form.note} onChange={(e) => set('note', e.target.value)} rows={2} /></Field>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <Field label={t('articles.fields.allowanceCharge')}><Input type="number" step="0.01" value={form.allowance_charge_amount} onChange={(e) => set('allowance_charge_amount', e.target.value)} style={{ width: 130 }} /></Field>
                <button type="button" onClick={() => set('allowance_charge_is_percentage', !form.allowance_charge_is_percentage)} style={{ height: 44, width: 44, borderRadius: 'var(--radius-field)', border: '1.5px solid var(--color-border-strong)', background: '#fff', fontWeight: 700, marginBottom: 16 }}>{form.allowance_charge_is_percentage ? '%' : '€'}</button>
                <button type="button" onClick={() => set('is_charge', !form.is_charge)} style={{ height: 44, padding: '0 14px', borderRadius: 'var(--radius-field)', border: '1.5px solid var(--color-border-strong)', background: '#fff', fontWeight: 600, marginBottom: 16, color: form.is_charge ? 'var(--color-info)' : 'var(--color-success)' }}>{form.is_charge ? t('articles.charge') : t('articles.discount')}</button>
                <Field label={t('articles.fields.reason')}><Input value={form.allowance_charge_reason} onChange={(e) => set('allowance_charge_reason', e.target.value)} style={{ minWidth: 160 }} /></Field>
              </div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 24px 22px', borderTop: '1px solid var(--color-border-row)', marginTop: 8 }}>
          <Button type="button" variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" icon="check" loading={submitting}>{t('common.save')}</Button>
        </div>
      </form>
    </Modal>
  )
}
